import { useId } from 'react'
import { showcaseSites, type ShowcaseProject, type ShowcaseProjectId } from '@/content/site'
import { cx } from '@/lib/cx'
import { SITE_ART } from './art'
import styles from './MiniSite.module.css'

/** `desktop` fills a browser frame; `phone` is the shop's mobile layout. */
export type MiniSiteLayout = 'desktop' | 'phone'

/** Props for {@link MiniSite}. */
export interface MiniSiteProps {
  project: ShowcaseProject & { readonly id: ShowcaseProjectId }
  layout: MiniSiteLayout
}

/** A generic chat bubble (never a messaging brand's logo). */
function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M4.5 6.2A2.7 2.7 0 0 1 7.2 3.5h9.6a2.7 2.7 0 0 1 2.7 2.7v7a2.7 2.7 0 0 1-2.7 2.7h-6.1l-4 3.3c-.5.4-1.2 0-1.2-.6v-2.7a2.7 2.7 0 0 1-1-2.1Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <circle cx="8.6" cy="9.8" r="1.1" fill="currentColor" />
      <circle cx="12" cy="9.8" r="1.1" fill="currentColor" />
      <circle cx="15.4" cy="9.8" r="1.1" fill="currentColor" />
    </svg>
  )
}

/**
 * One concept mini-site, drawn entirely with HTML, CSS and SVG in its shop's own art
 * direction. It is a picture of a website: no links, buttons or headings inside, and it is
 * `aria-hidden` (the gallery card names it). Everything scales with its frame through
 * container query units, so the same site renders crisply at any card size.
 * `[data-depth]` layers drift for the gallery's parallax.
 */
export function MiniSite({ project, layout }: MiniSiteProps) {
  const uid = useId().replace(/:/g, '')
  const copy = showcaseSites[project.id]
  const art = SITE_ART[project.id]
  const script = 'script' in copy ? copy.script : undefined

  return (
    <div
      className={cx(styles.site, styles[layout], styles[art.theme])}
      lang={copy.lang}
      aria-hidden="true"
    >
      <div className={styles.nav}>
        <span className={styles.logo}>
          <art.Logo uid={`${uid}l`} />
        </span>
        <span className={styles.brand}>{project.name}</span>
        <span className={styles.links}>
          {copy.nav.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </span>
        <span className={styles.navCta}>
          <ChatIcon />
          <span className={styles.navCtaLabel}>{copy.cta}</span>
        </span>
      </div>

      <div className={styles.hero}>
        <div className={styles.ornament} data-depth="0.5">
          <art.Ornament uid={`${uid}o`} />
        </div>
        <div className={styles.copy}>
          <span className={styles.kicker}>
            {project.trade} · {project.city}
          </span>
          {script ? (
            <span className={styles.script} lang={script.lang}>
              {script.text}
            </span>
          ) : null}
          <span className={styles.hook}>{project.hook}</span>
          <span className={styles.button}>
            <ChatIcon />
            {copy.cta}
          </span>
        </div>
        <div className={styles.art} data-depth="1">
          <art.Hero uid={`${uid}h`} />
        </div>
      </div>

      <div className={styles.products}>
        {copy.products.map((product, index) => {
          const Product = art.Products[index]
          return (
            <span key={product} className={styles.product}>
              <span className={styles.thumb}>
                {Product ? <Product uid={`${uid}p${index}`} /> : null}
              </span>
              <span className={styles.productName}>{product}</span>
            </span>
          )
        })}
      </div>
    </div>
  )
}
